// Eskom PARCC — Transactions register (read-only list + detail).
//
// The monolith's Transactions screen, rebuilt: a filterable/sortable register on
// the left and a read-only detail panel for the selected transaction on the
// right (the `.split` two-column layout). It fetches the full transaction +
// finding population (read-only, all roles see the whole register, as the
// monolith did), computes each transaction's effective compliance once (reusing
// the domain `calculateTransactionCompliance` via `effectiveCompliance`), then
// filters/sorts/selects entirely client-side. No writes — editing is Phase 3.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  SmartList,
  StatusIcon,
  StatusLegend,
  PersonChip,
  LoadingState,
  ErrorState,
  EmptyState,
  Pager,
  Skeleton,
  SplitSurface,
  usePagination,
  useToast,
  type SortOption,
} from '../../components/ui';
import { useTransactions, useFindings, useNameDirectory, useCesRowsByRef } from '../../hooks';
import { useAuth } from '../../lib/auth';
import { formatRand } from '../../lib/money';
import type { NameDirectory } from '../../lib/people';
import type { FindingRead } from '../../lib/db';
import {
  EMPTY_FACETS,
  matchesFacets,
  facetHaystack,
  type FacetState,
  type FacetKey,
} from '../../domain/filters';
import { FacetFilterBar } from '../../components/ui/FacetFilterBar';
import { scopeTransactions, canBulkDownloadWorkingPapers } from '../../lib/permissions';
import { buildAllTransactionsSheets, workbookFromSheets, downloadWorkbook } from '../exports';
import { effectiveCompliance } from './compliance';
import { genuineFindings } from '../../domain/findings';
import { canEditTransaction, isReviewed } from './editTransaction';
import { TransactionEditor } from './TransactionEditor';
import { type TransactionRow } from './filterTransactions';
import { TransactionDetail } from './TransactionDetail';

const EMPTY_DIRECTORY: NameDirectory = new Map();

/** The facet keys a URL may carry — the dashboard's drill-through CTA links here
 *  pre-filtered, so the register must be able to read a facet set back. */
const FACET_KEYS = Object.keys(EMPTY_FACETS) as FacetKey[];

const TX_SORT_OPTIONS: SortOption<TransactionRow>[] = [
  { key: 'referenceNo', label: 'Reference', accessor: (r) => r.tx.referenceNo },
  { key: 'supplier', label: 'Supplier', accessor: (r) => r.tx.supplier ?? '' },
  { key: 'value', label: 'Value', accessor: (r) => r.tx.value },
  { key: 'division', label: 'Division', accessor: (r) => r.tx.division ?? '' },
  { key: 'compliance', label: 'Compliance', accessor: (r) => r.compliance },
];

/** A transaction as a compound row: status-icon lead + two info lines. */
function renderTransactionRow(r: TransactionRow, directory: NameDirectory): React.JSX.Element {
  const meta = [r.tx.dataset, r.tx.division, r.tx.mechanism].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  );
  return (
    <>
      <span className="srow-lead">
        <StatusIcon reviewed={isReviewed(r.tx)} />
        <StatusIcon value={r.compliance} />
      </span>
      <span className="srow-main">
        <span className="srow-line1">
          <span className="srow-ref">{r.tx.referenceNo}</span>
          <span className="srow-supplier">{r.tx.supplier ?? '—'}</span>
          <span className="srow-value">{formatRand(r.tx.value)}</span>
        </span>
        <span className="srow-line2">
          <span>{meta.join(' · ') || '—'}</span>
          {r.tx.assignedToEmail && (
            <>
              <span className="srow-sep">·</span>
              <PersonChip email={r.tx.assignedToEmail} directory={directory} />
            </>
          )}
        </span>
      </span>
    </>
  );
}

export function TransactionsPage(): React.JSX.Element {
  const transactionsQuery = useTransactions();
  const findingsQuery = useFindings();
  const directoryQuery = useNameDirectory();
  const { grant } = useAuth();
  const toast = useToast();
  // "Download all (Excel)" is a CROSS-POPULATION export — offered to the roles that
  // see (and may bulk-export) the whole population: admin / manager / dataCoordinator.
  // Team leads and reviewers are home-division-scoped, so the CES fetch is deferred
  // for them (they never see the button and shouldn't pay for the large pull).
  const canDownloadAll = !!grant && canBulkDownloadWorkingPapers(grant.role);
  const cesByRefQuery = useCesRowsByRef(canDownloadAll);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [facets, setFacets] = useState<FacetState>(EMPTY_FACETS);
  const [search, setSearch] = useState('');
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // Lumen opens the detail as a 65% flyout, so it needs to know the reader ASKED
  // for it; Legacy shows its second column unconditionally, exactly as before.
  const [detailOpen, setDetailOpen] = useState(false);
  // A manual refresh shows a skeleton over the list rather than blanking it.
  const [refreshing, setRefreshing] = useState(false);

  const transactions = useMemo(
    () => (transactionsQuery.data ?? []).filter((t) => !t.deleted),
    [transactionsQuery.data],
  );

  // Owner feedback point 7: a non-admin sees ONLY their own allocations. Admins
  // see the whole register. This is the client boundary; the server-side scoped
  // query is the cryptographic one (activated on connector deploy).
  const visible = useMemo(() => scopeTransactions(transactions, grant), [transactions, grant]);

  // Honour a `?ref=` deep link (e.g. the Findings register's Reference
  // click-through): once the data has loaded, if the user has not yet selected a
  // row and the requested reference exists, pre-select it. Backward-compatible —
  // absent/unknown refs leave the existing first-row fallback untouched, and a
  // later user click (which sets `selectedRef`) always wins.
  const refParam = searchParams.get('ref');
  useEffect(() => {
    if (!refParam || selectedRef !== null) return;
    if (visible.some((t) => t.referenceNo === refParam)) {
      setSelectedRef(refParam);
      setDetailOpen(true); // a deep link means "show me this one"
    }
  }, [refParam, selectedRef, visible]);

  // Facets may also arrive in the URL (the dashboard drill's "open all N in the
  // register" CTA). Applied once per distinct query string, so a later manual
  // filter change is never fought by this effect.
  const facetQuery = FACET_KEYS.map((k) => `${k}=${searchParams.get(k) ?? ''}`).join('&');
  useEffect(() => {
    const incoming: FacetState = { ...EMPTY_FACETS };
    let any = false;
    for (const key of FACET_KEYS) {
      const value = searchParams.get(key);
      if (value) {
        incoming[key] = value;
        any = true;
      }
    }
    if (any) setFacets(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetQuery]);

  // Findings grouped by referenceNo, so the detail panel and the per-transaction
  // compliance roll-up both read a transaction's lines in O(1).
  const findingsByRef = useMemo(() => {
    const map = new Map<string, FindingRead[]>();
    for (const f of findingsQuery.data ?? []) {
      if (f.deleted || !f.referenceNo) continue;
      const bucket = map.get(f.referenceNo);
      if (bucket) bucket.push(f);
      else map.set(f.referenceNo, [f]);
    }
    return map;
  }, [findingsQuery.data]);

  // Pair every transaction with its effective compliance, computed once. Re-runs
  // only when the population or findings change — never per filter keystroke.
  const rows: TransactionRow[] = useMemo(
    () =>
      visible.map((tx) => ({
        tx,
        compliance: effectiveCompliance(tx, findingsByRef.get(tx.referenceNo) ?? []),
      })),
    [visible, findingsByRef],
  );

  // The shared facet model (feedback/Filters.xlsx) narrows the rows; the free-text
  // search runs over the same haystack the monolith used. Compliance is matched
  // against each row's pre-computed effective level (carried on the row).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => matchesFacets(r, facets) && (!q || facetHaystack(r.tx).includes(q)));
  }, [rows, facets, search]);

  // Keep the selection valid: if the selected ref is filtered out (or none is
  // chosen), fall back to the first visible row so the detail panel always shows.
  const selectedRow = useMemo(() => {
    if (filtered.length === 0) return null;
    return filtered.find((r) => r.tx.referenceNo === selectedRef) ?? filtered[0];
  }, [filtered, selectedRef]);

  // Close any open editor when the *resolved* selection changes (e.g. a filter
  // change re-points the fallback row, or a deep-link selects a different tx) so
  // the editor never lingers over a transaction the user did not choose to edit.
  const resolvedRef = selectedRow?.tx.referenceNo ?? null;
  useEffect(() => {
    setEditing(false);
  }, [resolvedRef]);

  const directory = directoryQuery.data ?? EMPTY_DIRECTORY;

  // Bound the rendered list. BOTH themes get a pager — the register runs to
  // thousands of rows — with Lumen on the handoff's calm 7–8 and Legacy on 50,
  // so an existing user's dense screen still reads the way it always has.
  const pagination = usePagination(filtered);

  // One-click download of the whole (visible) population as an XLSX that mirrors
  // the bulk-upload template and carries the review updates, plus per-line
  // Findings and CES sheets. Built entirely from data already in the cache — no
  // fetch on click — so "download what you can see" stays consistent with the view.
  const downloadAll = (): void => {
    try {
      const sheets = buildAllTransactionsSheets(
        visible,
        findingsQuery.data ?? [],
        cesByRefQuery.data ?? new Map(),
      );
      const date = new Date().toISOString().slice(0, 10);
      downloadWorkbook(`PARCC-all-transactions-${date}.xlsx`, workbookFromSheets(sheets));
      // The CES query is independent of the register: if it failed, the CES sheet
      // is empty — say so rather than let the file look complete.
      if (cesByRefQuery.isError) {
        toast.show(
          'Downloaded — but the CES Checklist sheet is empty: CES rows could not be loaded. Reload the page and try again.',
          'error',
        );
      }
    } catch (err) {
      toast.show(
        `Could not build the download: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  };

  /** Re-read the register from the server, with a skeleton over the list. */
  async function refresh(): Promise<void> {
    setRefreshing(true);
    try {
      await Promise.all([transactionsQuery.refetch(), findingsQuery.refetch()]);
      toast.show('Register refreshed.', 'success');
    } catch (err) {
      toast.show(`Could not refresh: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      setRefreshing(false);
    }
  }

  const isLoading = transactionsQuery.isLoading || findingsQuery.isLoading;
  const error = transactionsQuery.error ?? findingsQuery.error;

  if (isLoading) return <LoadingState message="Loading transactions…" />;
  if (error) {
    return (
      <ErrorState
        message="Could not load the transactions register."
        detail={error instanceof Error ? error.message : String(error)}
        onRetry={() => {
          void transactionsQuery.refetch();
          void findingsQuery.refetch();
        }}
      />
    );
  }
  if (transactions.length === 0) {
    return <EmptyState message="No transactions have been captured yet." />;
  }

  return (
    <>
      <FacetFilterBar
        rows={rows}
        state={facets}
        onChange={setFacets}
        onReset={() => {
          setFacets(EMPTY_FACETS);
          setSearch('');
        }}
        search={search}
        onSearch={setSearch}
      />

      <SplitSurface
        open={detailOpen && selectedRow !== null}
        onClose={() => {
          setDetailOpen(false);
          setEditing(false);
        }}
        title={selectedRow?.tx.referenceNo ?? 'Transaction'}
        subtitle={
          selectedRow
            ? [selectedRow.tx.dataset, selectedRow.tx.division, selectedRow.tx.period]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
        list={
          <div className="card">
            <div className="section-head">
              <div>
                <h3>Transactions</h3>
                <StatusLegend kind="compliance" />
              </div>
              <div className="actions">
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => void refresh()}
                  disabled={refreshing}
                  title="Re-read the register from the server"
                >
                  <RefreshCw size={14} aria-hidden /> {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
                {canDownloadAll && (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={downloadAll}
                    disabled={cesByRefQuery.isLoading}
                    title="Download every transaction as an Excel workbook — the population columns plus all review updates, with per-line Findings and CES sheets."
                  >
                    Download all (Excel)
                  </button>
                )}
              </div>
            </div>
            {refreshing ? (
              <Skeleton rows={pagination.pageSize} label="Refreshing transactions…" />
            ) : (
              <SmartList
                items={pagination.slice}
                getRowKey={(r) => r.tx.referenceNo || r.tx.id}
                renderRow={(r) => renderTransactionRow(r, directory)}
                sortOptions={TX_SORT_OPTIONS}
                initialSortKey="referenceNo"
                onRowClick={(r) => {
                  setEditing(false); // leave any open editor when switching rows
                  setSelectedRef(r.tx.referenceNo);
                  setDetailOpen(true);
                }}
                selectedRowKey={
                  selectedRow ? selectedRow.tx.referenceNo || selectedRow.tx.id : undefined
                }
                countLabel={
                  filtered.length === rows.length
                    ? `${rows.length.toLocaleString('en-ZA')} transaction(s)`
                    : `${filtered.length.toLocaleString('en-ZA')} of ${rows.length.toLocaleString('en-ZA')} transaction(s)`
                }
                emptyMessage="No transactions match the filters."
              />
            )}
            <Pager pagination={pagination} noun="transaction" />
          </div>
        }
        detail={
          selectedRow ? (
            (() => {
              const canEdit = canEditTransaction(selectedRow.tx, grant);
              // Raw rows feed the EDITOR (every captured line, incl. "Additional
              // comments" notes, stays editable); the read-only detail panel shows
              // and COUNTS only genuine findings — one per (tx × criterion), no
              // notes (owner feedback Aug 2026: the panel read 40, should read 26).
              const txFindings = findingsByRef.get(selectedRow.tx.referenceNo) ?? [];
              return editing && canEdit ? (
                <TransactionEditor
                  tx={selectedRow.tx}
                  findings={txFindings}
                  onClose={() => setEditing(false)}
                />
              ) : (
                <TransactionDetail
                  tx={selectedRow.tx}
                  compliance={selectedRow.compliance}
                  findings={genuineFindings(txFindings)}
                  canEdit={canEdit}
                  onEdit={() => setEditing(true)}
                  onCaptureCes={() =>
                    navigate(`/ces/${encodeURIComponent(selectedRow.tx.referenceNo)}`)
                  }
                />
              );
            })()
          ) : (
            <EmptyState message="Select a transaction to review its detail and finding lines." />
          )
        }
      />
    </>
  );
}
